import { useEffect, useState } from 'react';
import { Activity, ListFilter, Webhook, Key, Loader2 } from 'lucide-react';
import { useSession } from '../../lib/auth.js';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export default function Overview() {
  const { workspaces } = useSession();
  const ws = workspaces && workspaces[0];
  const [notices, setNotices] = useState([]);
  const [stats, setStats] = useState({ watchlists: 0, destinations: 0, keys: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ws) return;
    setLoading(true);

    const load = async () => {
      try {
        // Load sample notices for the feed.
        const nr = await fetch(`${API_BASE}/v1/sample/notices`, { credentials: 'include' });
        if (nr.ok) {
          const nd = await nr.json();
          setNotices((nd.data || []).slice(0, 10));
        }

        // Load stats.
        const [wlr, kr] = await Promise.all([
          fetch(`${API_BASE}/v1/watchlists?workspace_id=${ws.id}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : { watchlists: [] }),
          fetch(`${API_BASE}/v1/keys?workspace_id=${ws.id}`, { credentials: 'include' }).then((r) => r.ok ? r.json() : { keys: [] }),
        ]);
        const wls = wlr.watchlists || [];
        let destCount = 0;
        for (const wl of wls) {
          const dr = await fetch(`${API_BASE}/v1/watchlists/${wl.id}/destinations`, { credentials: 'include' });
          if (dr.ok) {
            const dd = await dr.json();
            destCount += (dd.destinations || []).length;
          }
        }
        setStats({ watchlists: wls.length, destinations: destCount, keys: (kr.keys || []).length });
      } catch (_) { /* swallow */ }
      setLoading(false);
    };
    load();
  }, [ws?.id]);

  return (
    <div>
      <h1 style={title}>Overview</h1>

      <div style={tileRow}>
        <Tile icon={Activity} label="Status" value={ws?.status || ' - '} />
        <Tile icon={ListFilter} label="Watchlists" value={stats.watchlists} />
        <Tile icon={Webhook} label="Destinations" value={stats.destinations} />
        <Tile icon={Key} label="API Keys" value={stats.keys} />
      </div>

      <h2 style={sectionTitle}>Live Feed (sample notices)</h2>
      {loading ? (
        <div style={center}><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : notices.length === 0 ? (
        <p style={muted}>No sample notices available.</p>
      ) : (
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>Country</th>
              <th style={th}>Kind</th>
              <th style={th}>Title</th>
              <th style={th}>Published</th>
            </tr>
          </thead>
          <tbody>
            {notices.map((n, i) => (
              <tr key={n.notice_id || i}>
                <td style={td}>{n.country}</td>
                <td style={td}>{n.notice_kind}</td>
                <td style={{ ...td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {n.title?.values?.[n.title?.primary_lang] || ' - '}
                </td>
                <td style={td}>{n.published_at ? new Date(n.published_at).toLocaleDateString() : ' - '}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value }) {
  return (
    <div style={tile}>
      <Icon size={16} style={{ color: 'var(--accent)' }} />
      <span style={tileLabel}>{label}</span>
      <span style={tileValue}>{value}</span>
    </div>
  );
}

const title = { fontSize: 'var(--fs-22)', fontWeight: 600, color: 'var(--fg-0)', marginBottom: 'var(--space-5)' };
const sectionTitle = { fontSize: 'var(--fs-16)', fontWeight: 500, color: 'var(--fg-0)', marginBottom: 'var(--space-3)' };
const tileRow = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' };
const tile = { background: 'var(--bg-2)', border: '1px solid var(--line-1)', borderRadius: 'var(--r-2)', padding: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 4 };
const tileLabel = { fontSize: 'var(--fs-11)', color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wider)' };
const tileValue = { fontSize: 'var(--fs-22)', fontWeight: 600, color: 'var(--fg-0)' };
const center = { textAlign: 'center', padding: 'var(--space-8)', color: 'var(--fg-2)' };
const muted = { color: 'var(--fg-3)', fontSize: 'var(--fs-13)' };
const table = { width: '100%', borderCollapse: 'collapse' };
const th = { textAlign: 'left', fontSize: 'var(--fs-11)', color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wider)', padding: 'var(--space-2) var(--space-3)', borderBottom: '1px solid var(--line-1)' };
const td = { padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--fs-13)', color: 'var(--fg-1)', borderBottom: '1px solid var(--line-1)' };
